<template>
  <div class="fc-wrap">
    <div class="fc-head">
      <div class="fc-logo">🥗</div>
      <div>
        <p class="fc-brand">MANZANA VERDE</p>
        <h1 class="fc-title">Forecast de Producción · On Demand</h1>
        <p class="fc-sub" v-if="data">
          {{ data.window.weeks }} semanas ({{ data.window.start }} → {{ data.window.end }}) ·
          Precantidad = proyección + 5% · generado {{ data.generatedAt }}
        </p>
      </div>
    </div>

    <div v-if="loading" class="fc-empty">Cargando forecast…</div>
    <div v-else-if="error" class="fc-empty">No se pudo cargar el forecast. {{ error }}</div>
    <div v-else-if="!kitchen" class="fc-empty">
      No hay una cocina On Demand asociada a tu usuario, así que no hay forecast que mostrar.
    </div>

    <template v-else>
      <!-- controles -->
      <div class="fc-controls">
        <div class="fc-field">
          <label>Cocina</label>
          <!-- El endpoint devuelve solo la cocina del usuario: con una sola no
               hay nada que elegir, se muestra como texto. El select queda para
               cuando la respuesta traiga mas de una. -->
          <select v-if="kitchensSorted.length > 1" v-model="kid" class="fc-select fc-w280">
            <option v-for="k in kitchensSorted" :key="k.id" :value="k.id">{{ k.name }} · {{ k.country }}</option>
          </select>
          <div v-else class="fc-kfixed">{{ kitchen.name }} · {{ kitchen.country }}</div>
          <div class="fc-kmeta">
            {{ fmt(curWeekOrders) }} pedidos/sem · {{ fmt(Math.round(weekUnits)) }} platos/sem ·
            {{ curDishes.length }} platos · canal {{ chLabel(channel) }}
          </div>
        </div>
        <div class="fc-field">
          <label>Semana</label>
          <select v-model.number="weekNum" class="fc-select fc-w240">
            <option v-for="w in weeksList" :key="w.num" :value="w.num">{{ w.label }}</option>
          </select>
        </div>
        <div class="fc-field">
          <label>Día a proyectar</label>
          <div class="fc-pills">
            <button class="fc-pill" :class="{on: day===-1, week: true}" @click="day=-1">
              Semana <span class="pd">{{ weekDates[0] }}–{{ weekDates[6] }}</span>
            </button>
            <button v-for="(dw,i) in data.dowLabels" :key="i" class="fc-pill" :class="{on: day===i}" @click="day=i">
              {{ abbr(dw) }} <span class="pd">{{ weekDates[i] }}</span>
            </button>
          </div>
        </div>
        <div class="fc-field">
          <label>Canal de venta</label>
          <div class="fc-pills">
            <button v-for="ch in data.channels" :key="ch" class="fc-pill"
              :class="{on: channel===ch}" :style="{opacity: chDishes(kitchen,ch).length ? 1 : 0.4}"
              @click="channel=ch">{{ chLabel(ch) }}</button>
          </div>
        </div>
      </div>

      <!-- KPIs -->
      <div class="fc-kpis">
        <div class="fc-kpi"><div class="k-l">Pedidos · {{ etiquetaDiaCorta }}</div>
          <div class="k-v">{{ fmt(Math.round(orders)) }}<span class="k-u">ped</span></div>
          <div class="k-n">canal {{ chLabel(channel) }}</div></div>
        <div class="fc-kpi"><div class="k-l">Precantidad total</div>
          <div class="k-v">{{ fmt(Math.round(totalUnits*BUFFER)) }}<span class="k-u">unid</span></div>
          <div class="k-n">proyección + 5% margen</div></div>
        <div class="fc-kpi"><div class="k-l">Platos distintos</div>
          <div class="k-v">{{ curDishes.length }}</div>
          <div class="k-n">con venta en {{ data.window.weeks }} sem</div></div>
        <div class="fc-kpi top"><div class="k-l">Plato top</div>
          <div class="k-v">{{ topDish ? fmt(Math.round(projOf(topDish))) : '—' }}<span class="k-u">u</span></div>
          <div class="k-n">{{ topDish ? topDish.name : '' }}</div></div>
      </div>

      <!-- tabla -->
      <div class="fc-panel">
        <div class="fc-panel-h">
          <h2>Platos a producir · {{ etiquetaDia }} · {{ chLabel(channel) }}</h2>
          <span class="fc-hint">{{ fmt(Math.round(orders)) }} pedidos · canal {{ chLabel(channel) }}</span>
        </div>
        <div class="fc-tools">
          <input class="fc-search" v-model="q" placeholder="Buscar plato o categoría…" />
          <select v-model="cat" class="fc-select">
            <option value="">Todas las categorías</option>
            <option v-for="c in cats" :key="c" :value="c">{{ c }}</option>
          </select>
          <button class="fc-btn" @click="downloadCsv">Descargar CSV</button>
        </div>
        <div class="fc-tscroll">
          <table class="fc-table">
            <thead>
              <tr>
                <th @click="sort('name')">Plato</th>
                <th class="r" @click="sort('proj')">Se venderán</th>
                <th class="r" @click="sort('prep')">Precantidad (+5%)</th>
                <th class="r" @click="sort('share')">% del día</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length"><td colspan="4" class="fc-empty">Sin platos para este día/filtro.</td></tr>
              <tr v-for="(r,idx) in rows" :key="idx">
                <td class="meal">{{ r.name }}</td>
                <td class="r">{{ fmtProj(r.proj) }}</td>
                <td class="r prep-cell"><b class="prep">{{ fmt(r.prep) }}</b></td>
                <td class="r">{{ fmt(r.share,1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="fc-foot">
          Método: nivel de los últimos 7 días abiertos × perfil del día de la semana (28 días)
          × share del plato (14 días), por canal. Los días cerrados y los feriados salen de la
          historia; en feriado se aplica un factor aprendido de los feriados pasados de la cocina.
          <b>Precantidad</b> = proyección × 1.05 (+5% de margen), por cocina y plato. Solo platos de la carta OnDemand.
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ForecastGateway from '@/gateways/ForecastGateway'

const ABBR = { 'Sábado': 'Sáb', 'Domingo': 'Dom', 'Lunes': 'Lun', 'Martes': 'Mar', 'Miércoles': 'Mié', 'Jueves': 'Jue', 'Viernes': 'Vie' }
const CHLAB = { propio: 'Propio', apps: 'App', todos: 'Todos' }
const BUFFER = 1.05

export default {
  name: 'ForecastList',
  data () {
    return {
      loading: true, error: null, data: null,
      kid: null, day: -1, channel: 'propio', q: '', cat: '',
      sortK: 'proj', sortDir: -1, weekNum: null, BUFFER, Math
    }
  },
  async mounted () {
    try {
      const res = await ForecastGateway.obtener()
      const p = (res.data && res.data.payload && res.data.payload.result) || res.data
      this.data = p
      if (p.kitchens && p.kitchens.length) {
        this.kid = p.kitchens.slice().sort((a, b) => b.weekOrders - a.weekOrders)[0].id
      }
      this.weekNum = this.curWeekNum
    } catch (e) {
      this.error = (e && e.message) || 'error'
    } finally {
      this.loading = false
    }
  },
  computed: {
    kitchensSorted () { return this.data ? this.data.kitchens.slice().sort((a, b) => b.weekOrders - a.weekOrders) : [] },
    kitchen () { return this.data ? this.data.kitchens.find(k => k.id === this.kid) : null },
    curDishes () { return this.chDishes(this.kitchen, this.channel) },
    weekUnits () { return this.curDishes.reduce((s, d) => s + d.wk, 0) },
    curWeekOrders () { let s = 0; for (let i = 0; i < 7; i++) s += this.curDowTot(this.kitchen, i); return s },
    orders () { return this.day < 0 ? this.curWeekOrders : this.curDowTot(this.kitchen, this.day) },
    // Las etiquetas de dia viven aca y no en el template a proposito: un `<`
    // dentro de {{ }} rompe el parser de vue-eslint (invalid-first-character-
    // of-tag-name), aunque webpack igual compile.
    etiquetaDia () { return this.day === -1 ? 'semana' : this.data.dowLabels[this.day] },
    etiquetaDiaCorta () { return this.day === -1 ? 'semana' : this.abbr(this.data.dowLabels[this.day]) },
    totalUnits () { return this.curDishes.reduce((s, d) => s + this.projOf(d), 0) },
    topDish () { return this.curDishes.slice().sort((a, b) => this.projOf(b) - this.projOf(a))[0] },
    cats () { return [...new Set(this.curDishes.map(d => d.cat || '').filter(Boolean))].sort((a, b) => a.localeCompare(b)) },
    rows () {
      let rows = this.curDishes.map(d => { const p = this.projOf(d); return { name: d.name, cat: d.cat, proj: p, prep: Math.ceil(p * BUFFER) } }).filter(r => r.proj > 0)
      const tot = rows.reduce((s, r) => s + r.proj, 0)
      rows.forEach(r => { r.share = tot > 0 ? r.proj / tot * 100 : 0 })
      if (this.cat) rows = rows.filter(r => (r.cat || '') === this.cat)
      const q = (this.q || '').toLowerCase().trim()
      if (q) rows = rows.filter(r => r.name.toLowerCase().includes(q) || (r.cat || '').toLowerCase().includes(q))
      rows.sort((a, b) => { const x = a[this.sortK], y = b[this.sortK]; return typeof x === 'string' ? this.sortDir * x.localeCompare(y) : this.sortDir * (x - y) })
      return rows
    },
    // semana MV (sáb-vie), numeración del año
    curSat () { const x = new Date(); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 1) % 7)); return x },
    curYear () { const f = new Date(this.curSat); f.setDate(this.curSat.getDate() + 6); return f.getFullYear() },
    anchor () { const a = new Date(this.curYear, 0, 1); a.setDate(a.getDate() - ((a.getDay() + 1) % 7)); return a },
    curWeekNum () { return Math.floor((this.curSat - this.anchor) / (7 * 864e5)) + 1 },
    weeksList () {
      const out = []
      for (let w = 1; w <= 53; w++) {
        const sat = new Date(this.anchor); sat.setDate(this.anchor.getDate() + (w - 1) * 7)
        const fri = new Date(sat); fri.setDate(sat.getDate() + 6)
        if (fri.getFullYear() !== this.curYear) continue
        out.push({ num: w, sat, label: `Semana ${w} · ${this.fmtD(sat)}–${this.fmtD(fri)}` })
      }
      return out
    },
    weekDates () {
      const w = this.weeksList.find(x => x.num === this.weekNum) || { sat: this.curSat }
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(w.sat); d.setDate(w.sat.getDate() + i); return this.fmtD(d) })
    }
  },
  methods: {
    fmt (n, d = 0) { return Number(n).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d }) },
    // Un decimal para volumenes chicos, entero para el resto.
    fmtProj (n) { return this.fmt(n, n < 10 ? 1 : 0) },
    fmtD (d) { return `${d.getDate()}/${d.getMonth() + 1}` },
    abbr (dw) { return ABBR[dw] || dw },
    chLabel (ch) { return CHLAB[ch] || ch },
    chDishes (k, ch) { return !k ? [] : (ch === 'propio' ? k.dishes : ((k.ch && k.ch[ch] && k.ch[ch].dishes) || [])) },
    chDow (k, ch, i) { return !k ? 0 : (ch === 'propio' ? k.dowOrders[i].total : ((k.ch && k.ch[ch] && k.ch[ch].dow[i]) || 0)) },
    curDowTot (k, i) { return this.chDow(k, this.channel, i) },
    projOf (dish) { return this.day < 0 ? dish.wk : dish.d[this.day] },
    sort (k) { if (this.sortK === k) this.sortDir *= -1; else { this.sortK = k; this.sortDir = (k === 'name' || k === 'cat') ? 1 : -1 } },
    downloadCsv () {
      const head = ['Plato', 'Se_venderan', 'Precantidad_+5pct', 'Pct_dia']
      const lines = [head.join(',')].concat(this.rows.map(r => [`"${r.name.replace(/"/g, "'")}"`, this.fmt(r.proj, 1), r.prep, this.fmt(r.share, 1)].join(',')))
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
      const dl = this.day < 0 ? 'semana' : this.data.dowLabels[this.day]
      a.download = `forecast_${(this.kitchen.name || '').replace(/[^a-z0-9]+/gi, '_')}_${this.channel}_${dl}.csv`; a.click()
    }
  }
}
</script>

<style scoped>
.fc-wrap { max-width: 1140px; margin: 0 auto; padding: 22px; color: #1a2420; font-size: 15px; }
.fc-head { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.fc-logo { width: 46px; height: 46px; border-radius: 13px; background: linear-gradient(135deg, #5DBC85, #3d9e68); display: grid; place-items: center; font-size: 22px; }
.fc-brand { font-size: 11px; font-weight: 800; letter-spacing: .16em; color: #3d9e68; margin: 0; }
.fc-title { font-size: 21px; font-weight: 720; margin: 0; }
.fc-sub { color: #5c6b63; font-size: 12.5px; margin: 2px 0 0; }
.fc-controls { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; background: #fff; border: 1px solid #e2e8e3; border-radius: 14px; padding: 16px 18px; box-shadow: 0 4px 16px rgba(26,36,32,.04); margin-bottom: 18px; }
.fc-field label { font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .06em; color: #8a988f; display: block; margin-bottom: 6px; }
.fc-select { appearance: none; background: #f0f4f1; border: 1.5px solid #e2e8e3; color: #1a2420; font: inherit; font-weight: 600; padding: 10px 14px; border-radius: 10px; cursor: pointer; }
.fc-w280 { min-width: 280px; } .fc-w240 { min-width: 240px; }
.fc-kfixed { min-width: 280px; font-weight: 650; padding: 10px 0; }
.fc-kmeta { font-size: 12px; color: #5c6b63; margin-top: 4px; }
.fc-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.fc-pill { border: 1.5px solid #e2e8e3; background: #f0f4f1; color: #5c6b63; font: inherit; font-weight: 600; font-size: 13px; padding: 8px 12px; border-radius: 9px; cursor: pointer; }
.fc-pill.on { background: #5DBC85; border-color: #5DBC85; color: #fff; }
.fc-pill.week.on { background: #1a2420; border-color: #1a2420; }
.fc-pill .pd { opacity: .65; font-weight: 600; font-size: 11.5px; margin-left: 3px; }
.fc-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 18px; }
.fc-kpi { background: #fff; border: 1px solid #e2e8e3; border-radius: 14px; padding: 15px 16px; position: relative; overflow: hidden; box-shadow: 0 4px 16px rgba(26,36,32,.04); }
.fc-kpi::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: #5DBC85; }
.fc-kpi.top::before { background: #FFD633; }
.k-l { font-size: 11px; font-weight: 640; text-transform: uppercase; letter-spacing: .05em; color: #8a988f; margin-bottom: 7px; }
.k-v { font-size: 26px; font-weight: 720; }
.k-u { font-size: 13px; font-weight: 600; color: #5c6b63; margin-left: 3px; }
.k-n { font-size: 11.5px; color: #5c6b63; margin-top: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fc-panel { background: #fff; border: 1px solid #e2e8e3; border-radius: 14px; box-shadow: 0 4px 16px rgba(26,36,32,.04); overflow: hidden; }
.fc-panel-h { padding: 15px 18px 0; display: flex; align-items: baseline; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.fc-panel-h h2 { font-size: 15px; font-weight: 660; margin: 0; }
.fc-hint { font-size: 12px; color: #8a988f; }
.fc-tools { display: flex; gap: 10px; padding: 12px 18px 14px; flex-wrap: wrap; }
.fc-search { flex: 1; min-width: 180px; background: #f0f4f1; border: 1.5px solid #e2e8e3; font: inherit; font-size: 14px; padding: 9px 13px; border-radius: 9px; }
.fc-btn { background: #f0f4f1; border: 1.5px solid #e2e8e3; font: inherit; font-weight: 600; font-size: 13px; padding: 9px 14px; border-radius: 9px; cursor: pointer; }
.fc-tscroll { overflow-x: auto; }
.fc-table { border-collapse: collapse; width: 100%; font-size: 14px; }
.fc-table th { text-align: left; font-size: 11px; font-weight: 660; text-transform: uppercase; letter-spacing: .04em; color: #8a988f; padding: 10px 14px; border-bottom: 1.5px solid #e2e8e3; cursor: pointer; white-space: nowrap; }
.fc-table th.r, .fc-table td.r { text-align: right; }
.fc-table td { padding: 10px 14px; border-bottom: 1px solid #e2e8e3; font-variant-numeric: tabular-nums; }
.fc-table td.meal { font-weight: 600; }
.prep-cell { background: rgba(93,188,133,.08); } .prep { color: #3d9e68; font-weight: 750; }
.fc-foot { font-size: 11.5px; color: #8a988f; padding: 14px 18px; line-height: 1.55; }
.fc-empty { padding: 30px 18px; text-align: center; color: #5c6b63; }
</style>
